/**
 * Message descriptors: the way to keep text that is decided now but shown later.
 *
 * A stored string freezes the language it was translated in. Stores, React state and data builders
 * therefore keep a `Msg` (key + params) and the component translates it at render with `useMsg()`.
 * See "Never store translated text" in docs/i18n.md.
 */
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

import { i18n } from './index.ts';
import type { viResources } from './resources.ts';

/** Any `ns:key` that exists in the vi resources (checked by tsc). */
type Resources = typeof viResources;
export type TypedKey = { [N in keyof Resources & string]: `${N}:${ParseKeys<N>}` }[keyof Resources & string];

export type MsgParams = Record<string, unknown>;

/** Key plus interpolation params; params may themselves hold a Msg (resolved when rendered). */
export type KeyMsg = { readonly key: TypedKey; readonly params?: MsgParams };

/** Parts translated one by one and joined, for lines like "Task • Submitted 08:00" whose pieces are Msgs. */
export type JoinMsg = { readonly join: readonly MaybeMsg[]; readonly separator: string };

export type Msg = KeyMsg | JoinMsg;

/** Backend or user text flows through as a plain string, app text as a Msg. */
export type MaybeMsg = string | Msg;

export function msg(key: TypedKey, params?: MsgParams): Msg {
  return params === undefined ? { key } : { key, params };
}

/** Joins the non-empty parts; each part is a plain string or a Msg. */
export function joinMsg(parts: ReadonlyArray<MaybeMsg | null | undefined | false>, separator = ', '): JoinMsg {
  return { join: parts.filter((part): part is MaybeMsg => Boolean(part)), separator };
}

export function isMsg(value: unknown): value is Msg {
  if (typeof value !== 'object' || value === null || '$$typeof' in value) return false;
  const shape = value as { key?: unknown; join?: unknown };
  return typeof shape.key === 'string' || Array.isArray(shape.join);
}

function resolveParams(params: MsgParams | undefined): MsgParams | undefined {
  if (!params) return undefined;
  const out: MsgParams = {};
  for (const name of Object.keys(params)) {
    const value = params[name];
    out[name] = isMsg(value) ? translateMsg(value) : value;
  }
  return out;
}

/** Translate now, in the current language. Call it at render time, never to store the result. */
export function translateMsg(value: MaybeMsg): string;
export function translateMsg(value: MaybeMsg | null | undefined): string | null;
export function translateMsg(value: MaybeMsg | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if ('join' in value) return value.join.map((part) => translateMsg(part)).filter(Boolean).join(value.separator);
  return (i18n.t as (key: string, options?: MsgParams) => string)(value.key, resolveParams(value.params));
}

/** Component hook: subscribes to language changes, so a stored Msg re-renders in the new language. */
export function useMsg(): (value: MaybeMsg | null | undefined) => string {
  useTranslation();
  return (value) => translateMsg(value ?? '') ?? '';
}

/**
 * The active language code, for `useMemo`/`useCallback` deps. A memo that calls a builder which
 * translates (`buildX()` using i18n.t) must list it, otherwise the cached text outlives a switch.
 * Prefer storing a Msg; use this only for values derived from translations at render time.
 */
export function useLanguage(): string {
  const { i18n: instance } = useTranslation();
  return instance.language;
}
